package com.swetdot.swetgram.data.cache

import com.swetdot.swetgram.cache.CachedChat
import com.swetdot.swetgram.cache.CachedMessage
import com.swetdot.swetgram.cache.CachedUser
import com.swetdot.swetgram.domain.Chat
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.domain.ChatKind
import com.swetdot.swetgram.domain.Message
import com.swetdot.swetgram.domain.MessageId
import com.swetdot.swetgram.domain.MessageKind
import com.swetdot.swetgram.domain.MessagePreview
import com.swetdot.swetgram.domain.MessageReaction
import com.swetdot.swetgram.domain.MessageState
import com.swetdot.swetgram.domain.User
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.protocol.ProtocolJson
import com.swetdot.swetgram.protocol.dto.MessageReaction as MessageReactionDto
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.serializer
import kotlin.time.Instant

/*
 * Cache rows <-> domain models.
 *
 * SQLite has no boolean or enum, so the schema stores INTEGER and TEXT and SQLDelight surfaces
 * Long and String. Converting here, in one place, keeps that detail out of the UI: a screen
 * should never see `isPinned == 1L`.
 *
 * Enum parsing is total. A row written by a NEWER build of the app (after an install rollback,
 * or a value added in a later release) must degrade to UNKNOWN rather than throw while the user
 * is scrolling their chat list.
 */

internal fun Boolean.toDb(): Long = if (this) 1L else 0L
internal fun Long.toBool(): Boolean = this != 0L

fun CachedUser.toDomain() = User(
    id = UserId(id),
    username = username,
    displayName = displayName,
    avatarUrl = avatarUrl,
    bio = bio,
    location = location,
    dateOfBirth = dateOfBirth,
    showBirthYear = showBirthYear.toBool(),
    isOnline = isOnline.toBool(),
    lastSeenAt = lastSeenAt?.let { Instant.fromEpochMilliseconds(it) },
    isDeleted = isDeleted.toBool(),
    hasBlackBadge = hasBlackBadge.toBool(),
)

fun User.toCacheRow() = CachedUser(
    id = id.value,
    username = username,
    displayName = displayName,
    avatarUrl = avatarUrl,
    bio = bio,
    location = location,
    dateOfBirth = dateOfBirth,
    showBirthYear = showBirthYear.toDb(),
    isOnline = isOnline.toDb(),
    lastSeenAt = lastSeenAt?.toEpochMilliseconds(),
    isDeleted = isDeleted.toDb(),
    hasBlackBadge = hasBlackBadge.toDb(),
)

fun CachedChat.toDomain(members: List<User>, lastMessage: Message?) = Chat(
    id = ChatId(id),
    kind = when (kind) {
        "GROUP" -> ChatKind.GROUP
        else -> ChatKind.DIRECT
    },
    title = title,
    avatarUrl = avatarUrl,
    members = members,
    lastMessage = lastMessage,
    unreadCount = unreadCount.toInt(),
    isMuted = isMuted.toBool(),
    isArchived = isArchived.toBool(),
    isPinned = isPinned.toBool(),
    updatedAt = Instant.fromEpochMilliseconds(updatedAt),
)

fun CachedMessage.toDomain(sender: User) = Message(
    // A message that has not been acknowledged yet has no server id; the client id is its
    // identity until one arrives.
    id = MessageId(serverId ?: clientId),
    clientId = clientId,
    chatId = ChatId(chatId),
    sender = sender,
    kind = when (kind) {
        "TEXT" -> MessageKind.TEXT
        "IMAGE" -> MessageKind.IMAGE
        "VOICE" -> MessageKind.VOICE
        "SYSTEM" -> MessageKind.SYSTEM
        "SHARED_POST" -> MessageKind.SHARED_POST
        else -> MessageKind.UNKNOWN
    },
    text = text,
    imageUrl = imageUrl,
    voiceUrl = voiceUrl,
    voiceDurationMs = voiceDurationMs,
    voiceWaveform = voiceWaveformJson?.let { json ->
        runCatching { ProtocolJson.decodeFromString(ListSerializer(Int.serializer()), json) }
            .getOrDefault(emptyList())
    } ?: emptyList(),
    replyTo = replyToId?.let {
        MessagePreview(
            MessageId(it), UserId(replyToSenderId.orEmpty()), replyToSenderDisplayName, replyToText,
            kind = when (replyToKind) {
                "IMAGE" -> MessageKind.IMAGE
                "VOICE" -> MessageKind.VOICE
                "TEXT" -> MessageKind.TEXT
                else -> MessageKind.UNKNOWN
            },
        )
    },
    state = when (state) {
        "SENDING" -> MessageState.SENDING
        "READ" -> MessageState.READ
        "FAILED" -> MessageState.FAILED
        else -> MessageState.SENT
    },
    sentAt = Instant.fromEpochMilliseconds(sentAt),
    editedAt = editedAt?.let { Instant.fromEpochMilliseconds(it) },
    isDeleted = isDeleted.toBool(),
    version = version.toInt(),
    canEdit = canEdit.toBool(),
    canDeleteForEveryone = canDeleteForEveryone.toBool(),
    reactions = reactionsJson?.let { json ->
        ProtocolJson.decodeFromString(ListSerializer(MessageReactionDto.serializer()), json)
            .map { MessageReaction(it.emoji, it.count, it.reactedByMe) }
    } ?: emptyList(),
    picso = picsoId?.let { id -> picsoExpiresAt?.let { com.swetdot.swetgram.domain.PicsoMessageReference(id, it) } },
)

fun List<MessageReaction>.toCacheJson(): String? =
    if (isEmpty()) null else ProtocolJson.encodeToString(
        ListSerializer(MessageReactionDto.serializer()),
        map { MessageReactionDto(it.emoji, it.count, it.reactedByMe) }
    )

fun List<Int>.toWaveformCacheJson(): String? =
    if (isEmpty()) null else ProtocolJson.encodeToString(ListSerializer(Int.serializer()), this)

fun ChatKind.toDb(): String = when (this) {
    ChatKind.GROUP -> "GROUP"
    ChatKind.DIRECT -> "DIRECT"
}

fun MessageKind.toDb(): String = name

fun MessageState.toDb(): String = name
